import {
	EFileType,
	FileTypeLabelEnum,
	FormError,
	PageTypeEnum,
} from '@/constant';
import { TDefineDocumentUploadListStore } from '@/stores/define-document-upload';
import { isTablet, randomId } from '@/utils';

import { Progress, Spin } from 'antd';

import { isNotNilOrEmpty } from 'ramda-adjunct';
import * as Yup from 'yup';
import { AnyObject } from 'yup';

import { DefinePreviewFileServerButton } from '..';
import Button from '../common/button';
import { ColumnTypes } from '../common/table/type';
import {
	DefineDocumentWriteModeEnum,
	PayloadUploaded,
} from '../define-document-write/define-document-write.type';
import PreviewFileButton from '../define-document-write/preview-file-button';
import ActionsDocument from './actions-document';
import RelationDocument from './relation-document';

export enum FileStatusEnum {
	UPLOADING = 'uploading',
	DONE = 'done',
	ERROR = 'error',
	WAITING = 'waiting',
	CONVERTING = 'converting',
}

export enum StatusDocumentUpload {
	ADD = 'add',
	DELETE = 'delete',
	UPDATE = 'update',
}

export const mappingInit = () => ({
	fileId: null as unknown as number,
	id: randomId(),
	refTableId: null as unknown as number,
	refTableName: '' as PageTypeEnum,
	registDt: '',
	status: StatusDocumentUpload.ADD,
});

type TColumnsDocument = {
	onDeleteDocument: (id: number) => void;
	onDeleteRow: (record: AnyObject) => void;
	addRowRelation: (record: AnyObject, index?: number) => void;
	subtractRelation: (id: number, record: AnyObject) => void;
	onChangeRelation: (value: string, record: AnyObject, id: number) => void;
	onChangeValue: (value: string, record: AnyObject, id: number) => void;
	onUploaded: (data: PayloadUploaded) => void;
	onSelectDocument: (record: AnyObject) => void;
	refTableId: number;
	selectedRowKeys: React.Key[];
	dataSource: AnyObject[];
};

const mappingItemSchema = Yup.object().shape({
	refTableName: Yup.mixed().test({
		name: 'required-if-visible',
		test: function (value, context) {
			const { from, parent } = context;
			const document = from?.[from.length - 2]?.value;

			const refTableId = parent?.refTableId;

			if (
				isNotNilOrEmpty(document?.fileList) &&
				!document?.hidden &&
				isNotNilOrEmpty(refTableId)
			) {
				return (
					(value !== null && value !== undefined) ||
					this.createError({ message: FormError.REQUIRED })
				);
			}
			return true;
		},
	}),
	refTableId: Yup.mixed()
		.nullable()
		.test({
			name: 'required-if-visible',
			test: function (value, context) {
				const { from, parent } = context;
				const document = from?.[from.length - 2]?.value;
				const refTableName = parent?.refTableName;
				if (
					isNotNilOrEmpty(document?.fileList) &&
					!document?.hidden &&
					isNotNilOrEmpty(refTableName)
				) {
					return (
						(value !== null && value !== undefined) ||
						this.createError({ message: FormError.REQUIRED })
					);
				}
				return true;
			},
		}),
});

const documentSchema = Yup.object().shape({
	mappingList: Yup.array().of(mappingItemSchema),
});

export const validationSchema = Yup.object().shape({
	documents: Yup.array().of(documentSchema),
});

export const getColumns = ({
	onDeleteDocument,
	onDeleteRow,
	addRowRelation,
	subtractRelation,
	onChangeRelation,
	onChangeValue,
	onUploaded,
	refTableId,
	selectedRowKeys,
	dataSource,
	onSelectDocument,
}: TColumnsDocument): ColumnTypes => [
	{
		title: '',
		dataIndex: 'addDocument',
		key: 'addDocument',
		width: 44,
	},
	{
		title: '파일 유형',
		dataIndex: 'typeName',
		key: 'typeName',
		width: isTablet ? 100 : 140,
		render: (value: string, record: AnyObject) => {
			return (
				<div className="flex items-center justify-between gap-1 w-[60px] 2xl:w-auto">
					<span className="text-[13px] font-medium">{value}</span>
					{value === FileTypeLabelEnum.FORM ? (
						<PreviewFileButton
							documentType={record?.documentType}
							rowId={record?.id}
							fileId={record?.fileList?.id}
							onUploaded={onUploaded}
							refTableName={record?.refTableName}
							refTableId={refTableId}
							mappingList={record?.mappingList}
							mode={DefineDocumentWriteModeEnum.UPDATE}
						/>
					) : value === FileTypeLabelEnum.UPLOAD ? (
						<DefinePreviewFileServerButton
							fileList={[
								{
									sourceType: EFileType.SERVER,
									file: record?.fileList,
								},
							]}
						/>
					) : null}
				</div>
			);
		},
	},
	{
		title: '파일',
		dataIndex: 'fileName',
		key: 'fileName',
		width: 280,
		render: (value: string, record: AnyObject) => {
			return (
				<ActionsDocument
					{...{
						record,
						onDeleteDocument,
						onUploaded,
						selectedRowKeys,
						onSelectDocument,
						refTableId,
					}}
				/>
			);
		},
	},
	{
		title: '연관 정보',
		dataIndex: 'mappingList',
		key: 'mappingList',
		width: isTablet ? 340 : 476,
		render: (
			values: TDefineDocumentUploadListStore[0]['mappingList'],
			record: AnyObject
		) => (
			<RelationDocument
				{...{
					values,
					record,
					addRowRelation,
					subtractRelation,
					onChangeRelation,
					onChangeValue,
					refTableId,
				}}
			/>
		),
	},
	{
		title: '등록일',
		dataIndex: 'registDt',
		key: 'registDt',
		width: 100,
		render: (value: string, record: AnyObject) => {
			if (record?.modified || !value) {
				return <span className="text-red8">미저장</span>;
			}
			return <span className="text-font2">{value}</span>;
		},
	},
	{
		title: '삭제',
		dataIndex: 'delete',
		key: 'delete',
		width: 65,
		render: (_: any, record: AnyObject) => {
			const checkEnableDeleteButton = (
				record: AnyObject,
				dataSource: AnyObject[]
			) =>
				dataSource.filter((item) => item.documentType === record.documentType)
					.length > 1;

			return (
				<Button
					className="!w-[43px] min-w-0"
					disabled={!checkEnableDeleteButton(record, dataSource)}
					onClick={() => onDeleteRow(record)}
				>
					삭제
				</Button>
			);
		},
	},
];

export const getColumnsUpload = (): ColumnTypes[number][] => [
	{
		title: 'NO.',
		dataIndex: 'no',
		key: 'no',
		width: 50,
		align: 'center',
	},
	{
		title: '파일',
		dataIndex: 'file',
		key: 'file',
		align: 'center',
		render: (value, record: AnyObject) => (
			<div className="relative flex items-center justify-center">
				<span className="pr-[50px] break-all">
					{record?.file?.name || record?.name}
				</span>
				{record.status === FileStatusEnum.CONVERTING && (
					<div className="w-7 h-7 absolute right-0">
						<Spin />
					</div>
				)}
				{![FileStatusEnum.CONVERTING, FileStatusEnum.WAITING].includes(
					record.status
				) && (
					<div className="absolute right-0">
						<Progress
							percent={record.progress}
							type="circle"
							status={record.status === 'error' ? 'exception' : undefined}
							size={28}
							strokeWidth={12}
						/>
					</div>
				)}
			</div>
		),
	},
];
